// Debug version of the full app with error boundaries
import { useState, useCallback, useEffect } from 'react';
import { ContentEditor } from './components/ContentEditor';
import { AnalysisPanel } from './components/AnalysisPanel';
import { Header } from './components/Header';
import { RealAIContentAnalyzer } from './services/RealAIContentAnalyzer';
import { ClaudeAPIService } from './services/ClaudeAPIService';
import { APIKeyConfig } from './components/APIKeyConfig';

interface ContentAnalysis {
  contentType: string;
  insights: ContentInsight[];
  chatbotResponse: string;
  analysisMetadata?: {
    processingTime: number;
    confidenceScore: number;
    researchBasis: string[];
    uniqueFindings: number;
    realTimeResearch: boolean;
    aiIntelligenceLevel: string;
  };
}

interface ContentInsight {
  id: string;
  lineNumber: number;
  originalText: string;
  issue: string;
  explanation: string;
  suggestion: string;
  improvedVersion: string;
  category: 'ai-citation-boost' | 'discovery-optimization' | 'authority-building' | 'structure-enhancement' | 'readability-enhancement' | 'semantic-enrichment' | 'user-engagement' | 'technical-accuracy' | 'conversion-optimization' | 'accessibility-improvement';
}

function App() {
  const [content, setContent] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<ContentAnalysis | null>(null);
  const [useClaudeAPI, setUseClaudeAPI] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check API configuration on app load
  useEffect(() => {
    const claudeService = ClaudeAPIService.getInstance();
    const isConfigured = claudeService.isApiConfigured();
    if (isConfigured) {
      console.log('🔍 API key detected on app load, enabling Claude API');
      setUseClaudeAPI(true);
    }
  }, []);

  const handleContentChange = useCallback((newContent: string) => {
    try {
      setContent(newContent);
      setError(null);
    } catch (err) {
      setError(`Content change error: ${err}`);
    }
  }, []);

  const handleAnalyze = useCallback(async () => {
    if (!content.trim() || content.trim().length < 20) return;
    
    setIsAnalyzing(true);
    setError(null);
    
    console.log(`🔍 Analysis Debug - useClaudeAPI: ${useClaudeAPI}`);
    const claudeService = ClaudeAPIService.getInstance();
    console.log(`🔍 Claude API configured: ${claudeService.isApiConfigured()}`);
    
    try {
      if (useClaudeAPI) {
        console.log('🤖 Using REAL Claude API for analysis...');
        const result = await claudeService.analyzeContentWithClaude(content);
        console.log('📊 Setting analysis result:', result);
        setAnalysis(result);
      } else {
        console.log('🚀 Using intelligent AI analysis system...');
        const analyzer = RealAIContentAnalyzer.getInstance();
        const result = await analyzer.analyzeContent(content);
        console.log('📊 Setting analysis result:', result);
        setAnalysis(result);
      }
    } catch (error) {
      const errorMsg = `Analysis failed: ${error}`;
      console.error(errorMsg);
      setError(errorMsg);
    } finally {
      setIsAnalyzing(false);
    }
  }, [content, useClaudeAPI]);

  try {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />

        <main className="max-w-7xl mx-auto px-4 py-6">
          {error && (
            <div style={{
              background: '#fee2e2',
              color: '#dc2626',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px'
            }}>
              Error: {error}
            </div>
          )}
          
          <APIKeyConfig onApiKeySet={(isConfigured) => {
            console.log(`🔄 API Key status changed: ${isConfigured}`);
            const wasUsingClaude = useClaudeAPI;
            setUseClaudeAPI(isConfigured);
            // Only clear analysis if we're switching between modes, not on every call
            if (wasUsingClaude !== isConfigured && analysis) {
              console.log('🧹 Clearing old analysis results due to API mode change');
              setAnalysis(null);
            }
          }} />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <ContentEditor
                content={content}
                onChange={handleContentChange}
                onAnalyze={handleAnalyze}
                isAnalyzing={isAnalyzing}
              />
            </div>

            <div>
              <AnalysisPanel
                analysis={analysis}
                isAnalyzing={isAnalyzing}
                onApplyImprovement={(lineNumber, improvedText) => {
                  try {
                    const lines = content.split('\n');
                    if (lineNumber > 0 && lineNumber <= lines.length) {
                      lines[lineNumber - 1] = improvedText;
                      const newContent = lines.join('\n');
                      setContent(newContent);
                      setAnalysis(null);
                    }
                  } catch (err) {
                    setError(`Apply improvement error: ${err}`);
                  }
                }}
              />
            </div>
          </div>
        </main>
      </div>
    );
  } catch (err) {
    return (
      <div style={{
        padding: '40px',
        backgroundColor: '#fee2e2',
        color: '#dc2626',
        minHeight: '100vh'
      }}>
        <h1>App Render Error</h1>
        <p>Error: {String(err)}</p>
      </div>
    );
  }
}

export default App;